'use strict';

const semver = require('semver');
const Enum = require('./enum');

/**
 * Minimum firmware version compatible with hyper bridge
 * @const {number}
 */
const MINIMUM_CONNECT_HYPER_BRIDGE_FIRMWARE_VERSION = '2.1.141';

/**
 * Minimum firmware version compatible with hyper bridge
 * @const {number}
 */
const MINIMUM_DOORBELL_HYPER_BRIDGE_FIRMWARE_VERSION = '2.3.0';

exports.DEFAULT_CONNECT_SERIAL_NUMBER = '0';

/**
 * Defines bridge events.
 *
 * @enum
 * @readonly
 */
exports.BridgeEvents = Object.create(Enum, {
  ONLINE: { value: 'online', enumerable: true },
  OFFLINE: { value: 'offline', enumerable: true },
  REMOTE_OPERATION: { value: 'remoteoperation', enumerable: true },
  ASSOCIATED_BRIDGE_ONLINE: { value: 'associated_bridge_online', enumerable: true },
  toStatus: {
    value: function(bridgeEvent) {
      switch (bridgeEvent) {
        case this.ONLINE:
        case this.ASSOCIATED_BRIDGE_ONLINE:
          return BridgeStatuses.ONLINE;
        case this.OFFLINE:
          return BridgeStatuses.OFFLINE;
        default:
          return BridgeStatuses.UNKNOWN;
      }
    },
    enumerable: false,
  },
});

/**
 * Defines bridge statuses.
 *
 * @enum
 * @readonly
 */
const BridgeStatuses = (exports.BridgeStatuses = Object.create(Enum, {
  ONLINE: { value: 'online', enumerable: true },
  OFFLINE: { value: 'offline', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },
}));

/**
 * Defines bridge device model types.
 *
 * @enum
 * @readonly
 */
const BridgeDeviceModelTypes = (exports.BridgeDeviceModelTypes = Object.create(Enum, {
  AUGUST_CONNECT: { value: 'august-connect', enumerable: true },
  AUGUST_DOORBELL: { value: 'august-doorbell', enumerable: true },
  AUGUST_LOCK: { value: 'august-lock', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },
  getModelTypeFromBridgeObject: {
    value: function(bridge = {}) {
      if (bridge.deviceModel) {
        return bridge.deviceModel;
      }

      const serialNumber = (bridge.mfgBridgeID || '').toUpperCase();
      switch (true) {
        case serialNumber.startsWith('C'):
          return this.AUGUST_CONNECT;
        case serialNumber.startsWith('D'):
          return this.AUGUST_DOORBELL;
        case serialNumber.startsWith('L'):
        case serialNumber.startsWith('M'):
          // M prefixes indicate a module; the cleaner approach
          // would be to make M its own bridge device model type,
          // 'MODULE', but that would require more changes
          // in Rest API & RBS. This change is to support Matrix
          // Reloaded which is a module mimicking Titan.
          return this.AUGUST_LOCK;
        default:
          return this.UNKNOWN;
      }
    },
  },
  hasClientCertificate: {
    value: function(bridge) {
      return bridge.deviceModel === BridgeDeviceModelTypes.AUGUST_LOCK;
    },
  },
  usesAuthJwt: {
    value: function(bridge) {
      return bridge.deviceModel === BridgeDeviceModelTypes.AUGUST_LOCK;
    },
  },
}));

/**
 * Defines capabilities for bridge firmware versions.
 *
 * @enum
 * @readonly
 */
exports.BridgeFirmwareVersion = Object.create(Enum, {
  isHyperBridgeSupported: {
    value: function(bridge) {
      const firmwareVersion = bridge.firmwareVersion;
      switch (bridge.deviceModel) {
        case BridgeDeviceModelTypes.AUGUST_CONNECT:
          return (
            semver.valid(firmwareVersion) && semver.gte(firmwareVersion, MINIMUM_CONNECT_HYPER_BRIDGE_FIRMWARE_VERSION)
          );
        case BridgeDeviceModelTypes.AUGUST_DOORBELL: {
          const coercedFirmwareVersion = semver.coerce(firmwareVersion);

          return (
            semver.valid(coercedFirmwareVersion) &&
            semver.gte(coercedFirmwareVersion, MINIMUM_DOORBELL_HYPER_BRIDGE_FIRMWARE_VERSION)
          );
        }
        case BridgeDeviceModelTypes.AUGUST_LOCK:
          return true;
        default:
          return false;
      }
    },
    enumerable: false,
  },
});
